import React, { useEffect, useContext } from 'react'
import { graphql } from 'gatsby'
import Helmet from 'react-helmet'
import SEO from '../components/SEO'
import Article from '../components/blog/Article'
import { useBlogPostViewedTracking } from '../hooks/tracking'
import { LayoutContext, useUI } from '../contexts'
import { useSchemaForLD } from '../hooks/useSchemaForLD'

const BlogPost = ({
  location,
  data: {
    contentfulBlogPost: {
      seoTitle,
      seoDescription,
      title,
      name,
      type,
      date,
      content,
      relatedBlogpost,
      image,
      verticals,
      slug,
    },
  },
}) => {
  const { node_locale, currency } = useContext(LayoutContext)

  useEffect(() => {
    useBlogPostViewedTracking({
      currency: currency?.code,
      locale: node_locale,
      name,
      title,
      verticals,
    })
  })

  const articleToString = (json) => {
    const strings = JSON.parse(json)
      .content.filter(
        ({ nodeType }) =>
          nodeType.includes('paragraph') || nodeType.includes('heading'),
      )
      .map(({ content }) => content[0].value)
    return strings.join(' ')
  }

  //--------------

  // ***** this is logic to set the modified date of the blog for SEO so it looks up to date *****
  const dateModified = new Date()
  dateModified.setDate(dateModified.getDate() - 1)

  //--------------

  return (
    <>
      <SEO
        isBlogPost
        title={seoTitle}
        description={seoDescription}
        location={location}
        image={image?.file && image.file.url}
        pageCategory={'Blog post'}
        referrer={location.state?.referrer}
        schemas={[
          useSchemaForLD('article', {
            image: image?.file && image.file.url,
            url: location.href,
            headline: title,
            name: title,
            datePublished: date,
            dateModified: dateModified,
            description: seoDescription,
            inLanguage: node_locale,
            keywords: title?.split(' '),
            articleBody: content?.json && articleToString(content.json),
          }),
        ]}
      />

      <Article
        title={title}
        type={type}
        date={date}
        content={content}
        relatedBlogpost={relatedBlogpost}
        location={location}
        slug={slug}
      />
    </>
  )
}

export const BlogPostQuery = graphql`
  query BlogPost($id: String!) {
    contentfulBlogPost(id: { eq: $id }) {
      seoTitle
      seoDescription
      slug
      title
      name
      type
      date
      image {
        file {
          url
        }
      }
      content {
        raw
        references {
          ... on ContentfulAsset {
            __typename
            contentful_id
            file {
              url
            }
            description
            title
          }
          ... on ContentfulVideo {
            contentful_id
            poster {
              file {
                url
              }
            }
            videoWebm {
              file {
                url
              }
            }
            videoMp4 {
              file {
                url
              }
            }
          }
        }
      }
      relatedBlogpost {
        availableInCountry
        title
        slug
        type
        image {
          file {
            url
          }
          fluid(sizes: "(max-width: 1023px) 45vw, 21vw") {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
      }
      verticals
    }
  }
`

export default BlogPost
